import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, ChevronDown, ChevronRight, Plus, Trash2 } from "lucide-react";

import { useModalContext } from "../../components/ModalProvider";
import {
  DocumentRole,
  DocumentRoleReference,
  useDocumentAnalysisAPI,
} from "../../DocumentAnalysisAPI";
import DocumentRoleEditor from "./DocumentRoleEditor";
import {
  CategoryBadge,
  EntityChip,
  OperatorChip,
  RefCodeMark,
  RelationLine,
  RoleReference,
  SharpnessMark,
} from "./documentRoleView";
import {
  RoleValidation,
  hasClassification,
  roleNames,
  subtypesWithoutClassification,
  validateRole,
} from "./ontologyValidation";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

/**
 * The document types of the catalogue, one row per role.
 *
 * A wide table on purpose: what makes a role usable is not its name but everything hanging off it —
 * which subtypes exist, what they do to the state, what the role points back at, what it makes one
 * expect, and which entities and relations it carries. Those belong side by side, because the
 * question a reader has is almost always a comparison across roles rather than a look at one.
 *
 * A raw table rather than the `DataTable` wrapper: rows expand into an editor, and the wrapper has
 * no expansion.
 */

const CATEGORIES = ["Miete", "Betrieb", "Eigentum", "Bau", "Finanzen", "ESG"];

interface Properties {}

export default function DocumentTypesTab(_properties: Properties) {
  const { t } = useTranslation();
  const { pushError } = useModalContext() as any;
  const api = useDocumentAnalysisAPI();

  const [roles, setRoles] = useState<DocumentRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<string>("all");
  const [onlyInvalid, setOnlyInvalid] = useState(false);
  const [onlyUnclassified, setOnlyUnclassified] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newCategory, setNewCategory] = useState(CATEGORIES[0]);

  const [retiring, setRetiring] = useState<{ role: DocumentRole; references: DocumentRoleReference[] } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.getDocumentRoles();
      setRoles(result.roles ?? []);
    } catch (error) {
      pushError(error);
    } finally {
      setLoading(false);
    }
    // The API object is rebuilt on every render; its behaviour is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * The validation of every role, computed once per load.
   *
   * It depends on the whole catalogue, not on one role: whether a reference hangs is decided by
   * which roles exist, so retiring one has to re-mark the others.
   */
  const known = useMemo(() => roleNames(roles), [roles]);
  const validations = useMemo(() => {
    const byName = new Map<string, RoleValidation>();
    roles.forEach((role) => byName.set(role.name, validateRole(role, known)));
    return byName;
  }, [roles, known]);

  const shown = useMemo(() => {
    const needle = search.trim().toLowerCase();

    return roles.filter((role) => {
      if (category !== "all" && role.category !== category) return false;

      if (onlyInvalid && (validations.get(role.name)?.invalidCount ?? 0) === 0) return false;

      // What a classification run cannot use yet — the roles to work through before starting one.
      if (onlyUnclassified && hasClassification(role) && subtypesWithoutClassification(role).length === 0)
        return false;

      if (!needle) return true;

      // The search covers the terms as well as the name: a reader looking for "Kaution" is looking
      // for the role that carries it, and does not know which one that is.
      const terms = [...(role.primaryEntities ?? []), ...(role.referenceEntities ?? [])].map(
        (entity) => entity.term,
      );

      return [role.name, ...terms].some((value) => String(value).toLowerCase().includes(needle));
    });
  }, [roles, search, category, onlyInvalid, onlyUnclassified, validations]);

  const create = async () => {
    try {
      const result = await api.createDocumentRole({ name: newName.trim(), category: newCategory });
      setCreateOpen(false);
      setNewName("");
      await load();
      setExpanded(result.role?.name ?? null);
    } catch (error) {
      pushError(error);
    }
  };

  const askToRetire = async (role: DocumentRole) => {
    try {
      const result = await api.getDocumentRoleReferences(role.name);
      setRetiring({ role, references: result.references ?? [] });
    } catch (error) {
      pushError(error);
    }
  };

  const retire = async () => {
    if (!retiring) return;

    try {
      await api.retireDocumentRole(retiring.role.name);
      setRetiring(null);
      if (expanded === retiring.role.name) setExpanded(null);
      await load();
    } catch (error) {
      pushError(error);
    }
  };

  const invalidTotal = useMemo(
    // `Array.from`, not a spread: the build targets ES5, where a Map iterator is not iterable.
    () => Array.from(validations.values()).reduce((total, validation) => total + validation.invalidCount, 0),
    [validations],
  );
  const unmappedTotal = useMemo(
    () => Array.from(validations.values()).reduce((total, validation) => total + validation.unmappedCount, 0),
    [validations],
  );

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[220px] flex-1">
          <Label htmlFor="documentRoleSearch">{t("documentAnalysisPanel.filter.search")}</Label>
          <Input
            id="documentRoleSearch"
            value={search}
            placeholder={t("documentAnalysisPanel.filter.searchPlaceholder")}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        <div>
          <Label htmlFor="documentRoleCategory">{t("documentAnalysisPanel.filter.category")}</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger id="documentRoleCategory" className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("documentAnalysisPanel.filter.allCategories")}</SelectItem>
              {CATEGORIES.map((entry) => (
                <SelectItem key={entry} value={entry}>
                  {entry}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button
          variant={onlyInvalid ? "default" : "outline"}
          aria-pressed={onlyInvalid}
          onClick={() => setOnlyInvalid(!onlyInvalid)}
        >
          <AlertTriangle className="mr-2 h-4 w-4" aria-hidden />
          {t("documentAnalysisPanel.filter.onlyInvalid")}
        </Button>

        <Button
          variant={onlyUnclassified ? "default" : "outline"}
          aria-pressed={onlyUnclassified}
          onClick={() => setOnlyUnclassified(!onlyUnclassified)}
        >
          {t("documentAnalysisPanel.filter.onlyUnclassified")}
        </Button>

        <div className="flex-1" />

        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" aria-hidden />
          {t("documentAnalysisPanel.newRole")}
        </Button>
      </div>

      <p className="text-sm text-muted-foreground">
        {t("documentAnalysisPanel.summary", {
          roles: roles.length,
          invalid: invalidTotal,
          unmapped: unmappedTotal,
        })}
      </p>

      {loading ? (
        <div className="flex flex-col gap-2">
          {[0, 1, 2, 3, 4].map((row) => (
            <Skeleton key={row} className="h-10 w-full" />
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8" />
                <TableHead>{t("documentAnalysisPanel.columns.category")}</TableHead>
                <TableHead>{t("documentAnalysisPanel.columns.role")}</TableHead>
                <TableHead>{t("documentAnalysisPanel.columns.refCodes")}</TableHead>
                <TableHead>{t("documentAnalysisPanel.columns.subtypes")}</TableHead>
                <TableHead>{t("documentAnalysisPanel.columns.defaultOperator")}</TableHead>
                <TableHead>{t("documentAnalysisPanel.columns.backRefs")}</TableHead>
                <TableHead>{t("documentAnalysisPanel.columns.expectationEdges")}</TableHead>
                <TableHead>{t("documentAnalysisPanel.columns.primaryEntities")}</TableHead>
                <TableHead>{t("documentAnalysisPanel.columns.referenceEntities")}</TableHead>
                <TableHead>{t("documentAnalysisPanel.columns.relations")}</TableHead>
                <TableHead>{t("documentAnalysisPanel.columns.notes")}</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={13} className="py-8 text-center text-sm text-muted-foreground">
                    {t("documentAnalysisPanel.noRoles")}
                  </TableCell>
                </TableRow>
              ) : (
                shown.map((role) => {
                  const validation = validations.get(role.name);
                  const open = expanded === role.name;

                  return (
                    <Fragment key={role.name}>
                      <TableRow className="align-top">
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            aria-expanded={open}
                            aria-label={t("documentAnalysisPanel.expand", { role: role.name })}
                            onClick={() => setExpanded(open ? null : role.name)}
                          >
                            {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </Button>
                        </TableCell>

                        <TableCell>
                          <CategoryBadge category={role.category} />
                        </TableCell>

                        <TableCell>
                          <div className="flex items-center gap-2 font-semibold">
                            {role.name}
                            {/* Grey, not red: a role without classification data is not wrong, it is
                                unfinished — and a run would simply have nothing to weigh it by. */}
                            {!hasClassification(role) ? (
                              <span
                                className="rounded-md bg-muted px-1.5 py-0.5 text-xs font-normal text-muted-foreground"
                                title={t("documentAnalysisPanel.unclassifiedHint")}
                              >
                                {t("documentAnalysisPanel.unclassified")}
                              </span>
                            ) : subtypesWithoutClassification(role).length > 0 ? (
                              <span
                                className="rounded-md bg-muted px-1.5 py-0.5 text-xs font-normal text-muted-foreground"
                                title={t("documentAnalysisPanel.subtypesUnclassifiedHint", {
                                  subtypes: subtypesWithoutClassification(role).join(", "),
                                })}
                              >
                                {t("documentAnalysisPanel.subtypesUnclassified", {
                                  count: subtypesWithoutClassification(role).length,
                                })}
                              </span>
                            ) : null}
                            {validation && validation.invalidCount > 0 ? (
                              <span
                                className="inline-flex items-center gap-1 rounded-md bg-destructive/10 px-1.5 py-0.5 text-xs text-destructive"
                                title={t("documentAnalysisPanel.invalidBadge", { count: validation.invalidCount })}
                              >
                                <AlertTriangle className="h-3 w-3" aria-hidden />
                                {validation.invalidCount}
                              </span>
                            ) : null}
                          </div>
                        </TableCell>

                        <TableCell>
                          <div className="flex flex-col gap-1">
                            {(role.refCodes ?? []).map((refCode, index) => (
                              <RefCodeMark key={index} {...refCode} />
                            ))}
                          </div>
                        </TableCell>

                        <TableCell>
                          <div className="flex flex-col gap-1">
                            {(role.subtypes ?? []).map((subtype, index) => (
                              <span key={index} className="flex items-center gap-1 text-xs">
                                {subtype.name}
                                {subtype.kind === "operator" ? (
                                  <OperatorChip
                                    operator={subtype.stateOperator}
                                    exTunc={subtype.exTunc}
                                    note={subtype.note}
                                  />
                                ) : (
                                  <span className="rounded border px-1 text-[11px] text-muted-foreground">Param</span>
                                )}
                              </span>
                            ))}
                          </div>
                        </TableCell>

                        <TableCell>
                          <OperatorChip
                            operator={role.stateOperatorDefault?.stateOperator}
                            note={role.stateOperatorDefault?.note}
                          />
                        </TableCell>

                        <TableCell>
                          <div className="flex flex-col gap-1">
                            {(role.backRefs ?? []).map((reference, index) => (
                              <RoleReference
                                key={index}
                                target={`→ ${reference.targetRole}`}
                                suffix={<SharpnessMark sharpness={reference.keySharpness} />}
                                issue={validation?.has("backRefs", index)}
                              />
                            ))}
                          </div>
                        </TableCell>

                        <TableCell>
                          <div className="flex flex-col gap-1">
                            {(role.expectationEdges ?? []).map((edge, index) => (
                              <RoleReference
                                key={index}
                                target={`⇒ ${edge.targetRole}`}
                                suffix={
                                  <span className="text-muted-foreground">
                                    ({[edge.trigger, edge.cadenceSource].filter(Boolean).join("; ")})
                                  </span>
                                }
                                issue={validation?.has("expectationEdges", index)}
                              />
                            ))}
                          </div>
                        </TableCell>

                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(role.primaryEntities ?? []).map((entity, index) => (
                              <EntityChip
                                key={index}
                                entity={entity}
                                issue={validation?.has("primaryEntities", index)}
                              />
                            ))}
                          </div>
                        </TableCell>

                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(role.referenceEntities ?? []).map((entity, index) => (
                              <EntityChip
                                key={index}
                                entity={entity}
                                issue={validation?.has("referenceEntities", index)}
                              />
                            ))}
                          </div>
                        </TableCell>

                        <TableCell>
                          <div className="flex flex-col gap-1">
                            {(role.relations ?? []).map((relation, index) => (
                              <RelationLine
                                key={index}
                                relation={relation}
                                issue={validation?.has("relations", index)}
                              />
                            ))}
                          </div>
                        </TableCell>

                        <TableCell className="max-w-[240px]">
                          <div className="flex flex-col gap-1 text-xs text-muted-foreground">
                            {role.keyNote ? <span>{role.keyNote}</span> : null}
                            {role.openWorldNote ? <span>{role.openWorldNote}</span> : null}
                            {role.clusterRole ? <span>{role.clusterRole}</span> : null}
                          </div>
                        </TableCell>

                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive"
                            aria-label={t("documentAnalysisPanel.retire", { role: role.name })}
                            onClick={() => askToRetire(role)}
                          >
                            <Trash2 className="h-4 w-4" aria-hidden />
                          </Button>
                        </TableCell>
                      </TableRow>

                      {open ? (
                        <TableRow>
                          <TableCell colSpan={13} className="bg-muted/30">
                            <DocumentRoleEditor
                              role={role}
                              knownRoles={known}
                              onSaved={async () => {
                                await load();
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("documentAnalysisPanel.newRole")}</DialogTitle>
          </DialogHeader>

          <div className="flex flex-col gap-3">
            <div>
              <Label htmlFor="newRoleName">{t("documentAnalysisPanel.columns.role")}</Label>
              <Input
                id="newRoleName"
                value={newName}
                placeholder="Mietvertragsdokument"
                onChange={(event) => setNewName(event.target.value)}
              />
              <p className="mt-1 text-xs text-muted-foreground">{t("documentAnalysisPanel.nameHint")}</p>
            </div>

            <div>
              <Label htmlFor="newRoleCategory">{t("documentAnalysisPanel.columns.category")}</Label>
              <Select value={newCategory} onValueChange={setNewCategory}>
                <SelectTrigger id="newRoleCategory">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((entry) => (
                    <SelectItem key={entry} value={entry}>
                      {entry}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              {t("global.cancel")}
            </Button>
            <Button disabled={newName.trim().length === 0} onClick={create}>
              {t("global.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!retiring} onOpenChange={(open) => (open ? null : setRetiring(null))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("documentAnalysisPanel.retireTitle", { role: retiring?.role.name })}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="flex flex-col gap-2">
                <span>{t("documentAnalysisPanel.retireExplanation")}</span>

                {/* Who would be left pointing at nothing. Shown, not resolved: cascading would
                    edit roles nobody asked to change. */}
                {retiring && retiring.references.length > 0 ? (
                  <div className="rounded-md border border-destructive/40 p-2 text-sm">
                    <div className="font-medium text-destructive">
                      {t("documentAnalysisPanel.retireReferences", { count: retiring.references.length })}
                    </div>
                    <ul className="mt-1 list-inside list-disc">
                      {retiring.references.map((reference, index) => (
                        <li key={index}>
                          {reference.name} <span className="text-muted-foreground">({reference.via})</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <span className="text-muted-foreground">{t("documentAnalysisPanel.retireNoReferences")}</span>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel>{t("global.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={retire}>{t("global.delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
